import { jsx, jsxs } from 'react/jsx-runtime';
import { useTimeout } from '@flex-design-system/fx-utils/hooks';
import { DismissableLayerBranch } from '@radix-ui/react-dismissable-layer';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { forwardRef, useCallback, useImperativeHandle, useEffect, useRef } from 'react';
import { getAutoClose } from './getAutoClose.mjs';
import { rootStyle, innerStyle, slotRootStyle, titleStyle } from './toast-content.vanilla.mjs';

function ToastContent({
  toast,
  role = "status",
  shouldHide,
  hide
}) {
  const timeoutContainerRef = useRef(null);
  const { autoClose = 3e3, content, leftSlot, rightSlot } = toast;
  useEffect(() => {
    if (shouldHide) {
      timeoutContainerRef.current?.hide();
    }
  }, [shouldHide]);
  return (
    /**
     * https://linear.app/flexteam/issue/FX-251/toastcontext에-dismissable-layer-scope-생성
     *
     * - modality가 있는 popper가 뜬 상태에서 생성된 toast에 대한 인터렉션이 outside click으로 인식되지 않게 하기 위함
     * - modality가 있는 popper가 뜬 상태에서도 toast의 인터렉션이 가능하게 하기 위해 pointer-events 재설정
     */
    /* @__PURE__ */ jsx(DismissableLayerBranch, { style: { pointerEvents: "auto" }, children: /* @__PURE__ */ jsx(
      TimeoutContainer,
      {
        ref: timeoutContainerRef,
        toast,
        onHide: hide,
        autoClose,
        role,
        "aria-live": role === "alert" ? "assertive" : "polite",
        children: /* @__PURE__ */ jsxs("div", { className: innerStyle, children: [
          leftSlot && /* @__PURE__ */ jsx(
            "div",
            {
              className: slotRootStyle({
                side: "left"
              }),
              children: leftSlot
            }
          ),
          /* @__PURE__ */ jsx("div", { className: titleStyle, children: content }),
          rightSlot && /* @__PURE__ */ jsx(
            "div",
            {
              className: slotRootStyle({
                side: "right"
              }),
              children: rightSlot
            }
          )
        ] })
      },
      toast.id
    ) })
  );
}
const TimeoutContainer = forwardRef(function TimeoutContainer2({
  className: classNameFromProps,
  children,
  toast,
  onHide,
  autoClose,
  ...props
}, ref) {
  const className = classNames(rootStyle, classNameFromProps);
  const { setTimeout, clearTimeout } = useTimeout({ clearOnNewTimeout: true });
  const hideToast = useCallback(() => {
    onHide(toast.id);
    clearTimeout();
  }, [onHide, toast.id, clearTimeout]);
  useImperativeHandle(
    ref,
    () => ({
      hide: hideToast
    }),
    [hideToast]
  );
  const autoCloseTimeout = getAutoClose(autoClose, toast);
  useEffect(() => {
    toast.onOpen?.(toast);
  }, [toast]);
  useEffect(() => {
    if (typeof autoCloseTimeout === "number") {
      setTimeout(hideToast, autoCloseTimeout);
      return clearTimeout;
    }
  }, [autoCloseTimeout, clearTimeout, hideToast, setTimeout, toast]);
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      className,
      initial: "initial",
      animate: "animate",
      exit: "exit",
      variants: {
        initial: {
          opacity: 1,
          scale: 0.95,
          y: "-60px",
          x: "-50%"
        },
        animate: {
          opacity: 1,
          x: "-50%",
          y: 0,
          transition: {
            duration: 0.45,
            ease: [0.2, 1, 0.8, 1]
          }
        },
        exit: {
          opacity: 0,
          scale: 0.8,
          y: 2,
          transition: {
            duration: 0.45,
            ease: [0.2, 1, 0.8, 1]
          }
        }
      },
      ...props,
      children
    }
  );
});

export { ToastContent };
//# sourceMappingURL=ToastContent.mjs.map
